import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, CreditCard, Gift, Loader2, ExternalLink } from "lucide-react";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: "bg-green-600/20 text-green-400 border-green-500/40",
  past_due: "bg-amber-600/20 text-amber-400 border-amber-500/40",
  canceled: "bg-zinc-600/20 text-zinc-400 border-zinc-500/40",
  pending: "bg-yellow-600/20 text-yellow-400 border-yellow-500/40",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export const MembershipCard = ({ membership, services, vehicles, onRefresh }) => {
  const [portalLoading, setPortalLoading] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [service, setService] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post(`${API}/membership/portal`, { origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open billing portal. Please call us.");
      setPortalLoading(false);
    }
  };

  const redeem = async () => {
    if (!service) return toast.error("Choose a service to redeem.");
    setRedeeming(true);
    try {
      const { data } = await axios.post(`${API}/membership/redeem`, { service, vehicle_id: vehicleId || null }, { withCredentials: true });
      toast.success(data.message);
      setRedeemOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Redemption failed.");
    } finally {
      setRedeeming(false);
    }
  };

  if (!membership) {
    return (
      <div className="bg-[#121212] border border-zinc-800 rounded-xl p-8" data-testid="portal-no-membership">
        <h3 className="font-heading text-lg font-semibold text-white mb-2">No Active Membership</h3>
        <p className="text-zinc-400 text-sm mb-5">
          Join Mason's Mobile Membership for a complimentary annual service, 10% off eligible labor, and priority scheduling.
        </p>
        <Link
          to="/membership"
          data-testid="portal-join-membership-button"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-3 font-semibold transition-colors"
        >
          Join for $39.99/month
        </Link>
      </div>
    );
  }

  const status = membership.status || "pending";
  return (
    <div className="bg-[#121212] border border-blue-500/30 rounded-xl p-8" data-testid="portal-membership-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <BadgeCheck className="h-6 w-6 text-blue-500" />
          <h3 className="font-heading text-lg font-semibold text-white">Mason's Mobile Membership</h3>
        </div>
        <Badge variant="outline" className={`${STATUS_STYLES[status] || STATUS_STYLES.pending} rounded-sm uppercase`} data-testid="membership-status-badge">
          {status.replace("_", " ")}
        </Badge>
      </div>

      {status === "past_due" && (
        <div className="bg-amber-600/10 border border-amber-500/40 text-amber-300 text-sm rounded-sm p-4 mb-6" data-testid="past-due-banner">
          Your last payment failed. Please update your payment method to keep your benefits active.
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-6 text-sm">
        <div>
          <div className="text-zinc-500">Monthly Price</div>
          <div className="text-white font-semibold">${Number(membership.price || 39.99).toFixed(2)}/mo</div>
        </div>
        <div>
          <div className="text-zinc-500">Member Since</div>
          <div className="text-white font-semibold">{fmt(membership.enrolled_at)}</div>
        </div>
        <div>
          <div className="text-zinc-500">{membership.cancel_at_period_end ? "Benefits End" : "Next Billing Date"}</div>
          <div className="text-white font-semibold" data-testid="membership-next-billing">{fmt(membership.current_period_end)}</div>
        </div>
      </div>

      <div className="border border-zinc-800 rounded-sm p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Gift className="h-5 w-5 text-blue-500" />
          <div>
            <div className="text-white text-sm font-semibold">Complimentary Annual Service</div>
            <div className="text-zinc-500 text-xs">
              {membership.complimentary_eligible ? "Available to redeem" : "Already redeemed this membership year"}
            </div>
          </div>
        </div>
        <button
          data-testid="redeem-service-button"
          disabled={!membership.complimentary_eligible}
          onClick={() => setRedeemOpen(true)}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-sm px-4 py-2 text-sm font-semibold transition-colors"
        >
          Redeem
        </button>
      </div>

      <button
        data-testid="manage-billing-button"
        onClick={openBillingPortal}
        disabled={portalLoading}
        className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-white rounded-sm px-5 py-2.5 text-sm font-semibold transition-colors"
      >
        {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
        Manage Billing / Cancel <ExternalLink className="h-3.5 w-3.5" />
      </button>
      <p className="text-zinc-600 text-xs mt-3">
        Update your payment method, view invoices, or cancel future renewals via the secure Stripe customer portal.
      </p>

      <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Redeem Complimentary Service</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-zinc-300 text-sm block mb-1.5">Choose ONE eligible service</label>
              <select
                data-testid="redeem-service-select"
                className="w-full h-11 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm"
                value={service}
                onChange={(e) => setService(e.target.value)}
              >
                <option value="">Select a service...</option>
                {services.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            {vehicles.length > 0 && (
              <div>
                <label className="text-zinc-300 text-sm block mb-1.5">Vehicle (optional)</label>
                <select
                  data-testid="redeem-vehicle-select"
                  className="w-full h-11 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm"
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                >
                  <option value="">Select a vehicle...</option>
                  {vehicles.map((v) => (
                    <option key={v.vehicle_id} value={v.vehicle_id}>{v.year} {v.make} {v.model}</option>
                  ))}
                </select>
              </div>
            )}
            <p className="text-zinc-500 text-xs">We'll contact you to schedule. One redemption per rolling 12 months. Filters must be purchased by the member for filter installations.</p>
          </div>
          <DialogFooter>
            <button
              data-testid="redeem-confirm-button"
              onClick={redeem}
              disabled={redeeming}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-6 py-2.5 font-semibold transition-colors"
            >
              {redeeming && <Loader2 className="h-4 w-4 animate-spin" />} Confirm Redemption
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
